import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Loader2, DollarSign, Zap, Image, Video, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const Costs = () => {
  const { currentProjectId, projectInfo } = useProject();
  const { api } = useAuth();
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentProjectId) return;
      try {
        const [usageData, historyData, costsData] = await Promise.all([
          api(`/api/usage/today?project_id=${currentProjectId}`),
          api(`/api/usage/history?project_id=${currentProjectId}&days=14`),
          api(`/api/usage/costs?project_id=${currentProjectId}`)
        ]);
        setUsage(usageData);
        setHistory(historyData.reverse());
        setCosts(costsData);
      } catch (err) {
        console.error('Failed to fetch usage:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentProjectId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="costs-page">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Costs & Usage</h1>
        <p className="text-muted-foreground">Monitor AI resource consumption and costs</p>
      </div>

      {/* Today's Usage */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <Badge variant="outline">{Math.round((usage?.tokens_used / usage?.caps?.tokens) * 100 || 0)}%</Badge>
            </div>
            <div className="text-2xl font-bold">{(usage?.tokens_used || 0).toLocaleString()}</div>
            <div className="text-sm text-muted-foreground">Tokens Today</div>
            <Progress value={(usage?.tokens_used / usage?.caps?.tokens) * 100 || 0} className="h-1.5 mt-2" />
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-glow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <Image className="w-5 h-5 text-amber-400" />
              </div>
              <Badge variant="outline">{usage?.images_generated || 0} / {usage?.caps?.images || 30}</Badge>
            </div>
            <div className="text-2xl font-bold">{usage?.images_generated || 0}</div>
            <div className="text-sm text-muted-foreground">Images Today</div>
            <Progress value={(usage?.images_generated / usage?.caps?.images) * 100 || 0} className="h-1.5 mt-2" />
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-glow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Video className="w-5 h-5 text-purple-400" />
              </div>
              <Badge 
                variant="outline" 
                className={(usage?.videos_generated || 0) >= (usage?.caps?.videos || 2) ? 'border-red-500 text-red-400' : ''}
              >
                {usage?.videos_generated || 0} / {usage?.caps?.videos || 2}
              </Badge>
            </div>
            <div className="text-2xl font-bold">{usage?.videos_generated || 0}</div>
            <div className="text-sm text-muted-foreground">Videos Today</div>
            <Progress 
              value={(usage?.videos_generated / usage?.caps?.videos) * 100 || 0} 
              className={`h-1.5 mt-2 ${(usage?.videos_generated || 0) >= (usage?.caps?.videos || 2) ? '[&>div]:bg-red-500' : ''}`}
            />
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-glow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            </div>
            <div className="text-2xl font-bold">${costs?.costs?.total || '0.00'}</div>
            <div className="text-sm text-muted-foreground">Est. 30-Day Cost</div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-card border-border card-glow">
          <CardHeader>
            <CardTitle className="text-base">Token Usage (14 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717a"
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#71717a" />
                  <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '4px' }} />
                  <Line type="monotone" dataKey="tokens_used" stroke={projectInfo.color} strokeWidth={2} dot={{ fill: projectInfo.color }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border card-glow">
          <CardHeader>
            <CardTitle className="text-base">Content Generated (14 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#71717a"
                    tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis stroke="#71717a" />
                  <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '4px' }} />
                  <Bar dataKey="images_generated" fill="#f59e0b" name="Images" />
                  <Bar dataKey="videos_generated" fill="#8b5cf6" name="Videos" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Summary */}
      <Card className="bg-card border-border card-glow">
        <CardHeader>
          <CardTitle className="text-base">Cost Breakdown (30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-black/30 rounded-lg text-center">
              <div className="text-sm text-muted-foreground mb-1">Tokens</div>
              <div className="text-xl font-bold">${costs?.costs?.tokens || '0.00'}</div>
              <div className="text-xs text-muted-foreground">{(costs?.usage?.tokens || 0).toLocaleString()} tokens</div>
            </div>
            <div className="p-4 bg-black/30 rounded-lg text-center">
              <div className="text-sm text-muted-foreground mb-1">Images</div>
              <div className="text-xl font-bold">${costs?.costs?.images || '0.00'}</div>
              <div className="text-xs text-muted-foreground">{costs?.usage?.images || 0} images</div>
            </div>
            <div className="p-4 bg-black/30 rounded-lg text-center">
              <div className="text-sm text-muted-foreground mb-1">Videos</div>
              <div className="text-xl font-bold">${costs?.costs?.videos || '0.00'}</div>
              <div className="text-xs text-muted-foreground">{costs?.usage?.videos || 0} videos</div>
            </div>
            <div className="p-4 bg-primary/10 rounded-lg text-center border border-primary/20">
              <div className="text-sm text-muted-foreground mb-1">Total</div>
              <div className="text-2xl font-bold text-primary">${costs?.costs?.total || '0.00'}</div>
              <div className="text-xs text-muted-foreground">Monthly Run Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Costs;
